import React, { useEffect, useRef, useState } from 'react';
import { collection, deleteDoc, doc, getDoc, getDocs, query, setDoc, Timestamp, updateDoc, where } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../../utils/firebase';
import { GAME_UPLOAD_CODE_COLLECTION, normalizeTeacherCode, toCodeIssuerGameUpdate } from '../../utils/arcadeGuestUpload';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useLayoutContext } from '../../components/layout/Layout';
import { getMakecodeArcadePageRootStyle } from './makecodeArcadePageStyles';
import MainLayout from '../../components/layout/MainLayout';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';

interface UploadCode {
  code: string;
  schoolId: string;
  schoolName: string;
  memo: string;
  createdBy: string;
  createdAt: Date;
}

interface CodeRegisteredGame {
  id: string;
  title: string;
  description: string;
  introduction: string;
  gameUrl: string;
  simulatorCode: string;
  processUrl: string;
  companyName: string;
  creatorName: string;
  grade: string;
  className: string;
  commentsOnly: boolean;
  allowGuestComments: boolean;
  uploadCode: string;
  iconUrl: string;
  createdAt: Date;
}

interface GameEditForm {
  title: string;
  description: string;
  introduction: string;
  gameUrl: string;
  simulatorCode: string;
  processUrl: string;
  companyName: string;
  creatorName: string;
  grade: string;
  className: string;
  commentsOnly: boolean;
  allowGuestComments: boolean;
}

const emptyGameForm: GameEditForm = {
  title: '',
  description: '',
  introduction: '',
  gameUrl: '',
  simulatorCode: '',
  processUrl: '',
  companyName: '',
  creatorName: '',
  grade: '',
  className: '',
  commentsOnly: false,
  allowGuestComments: false,
};

const fieldStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  border: '1px solid #F0F0F0',
  borderRadius: 8,
  fontSize: 14,
  fontFamily: 'Pretendard',
  boxSizing: 'border-box',
  backgroundColor: '#FFFFFF',
  color: '#161616',
};

const numberChoices = (max: number, current: string): string[] => {
  const values = Array.from({ length: max }, (_, index) => String(index + 1));
  if (current && !values.includes(current)) values.push(current);
  return values;
};

const UploadCodePage: React.FC = () => {
  const { currentUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { isMobile } = useLayoutContext();
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [codes, setCodes] = useState<UploadCode[]>([]);
  const [memo, setMemo] = useState('');
  const [affiliation, setAffiliation] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [registeredGames, setRegisteredGames] = useState<CodeRegisteredGame[]>([]);
  const [gamesLoading, setGamesLoading] = useState(false);
  const [editingGameId, setEditingGameId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<GameEditForm>(emptyGameForm);
  const [savingGame, setSavingGame] = useState(false);
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const iconInputRef = useRef<HTMLInputElement>(null);

  const isTeacherUser = !!currentUser && ['teacher', 'schoolAdmin', 'superAdmin'].includes(currentUser.role);

  useEffect(() => {
    if (authLoading) return;
    if (!currentUser) {
      navigate('/login', { state: { from: { pathname: '/makecodearcade/upload-code' }, suggestedRole: 'teacher' } });
      return;
    }
    if (!isTeacherUser) {
      navigate('/makecodearcade');
      return;
    }
    loadCodes();
  }, [currentUser, isTeacherUser, navigate, authLoading]);

  const loadCodes = async () => {
    if (!currentUser?.schoolId && currentUser?.role !== 'superAdmin') {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      if (currentUser.schoolId) {
        const schoolSnap = await getDoc(doc(db, 'schools', currentUser.schoolId));
        const fetchedName = schoolSnap.exists() ? (schoolSnap.data().name || '') : '';
        setAffiliation((prev) => (prev.trim() ? prev : fetchedName));
      }
      const codesRef = collection(db, GAME_UPLOAD_CODE_COLLECTION);
      const q = currentUser.role === 'superAdmin' && !currentUser.schoolId
        ? query(codesRef, where('createdBy', '==', currentUser.uid))
        : query(codesRef, where('schoolId', '==', currentUser.schoolId));
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map((item) => {
        const data = item.data();
        return {
          code: item.id,
          schoolId: data.schoolId || '',
          schoolName: data.schoolName || '',
          memo: data.memo || '',
          createdBy: data.createdBy || '',
          createdAt: data.createdAt?.toDate?.() || new Date(),
        };
      }).sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      setCodes(list);
      await loadRegisteredGames(list, currentUser.uid);
    } catch (loadError) {
      console.error('등록 코드 목록 오류:', loadError);
      setError('등록 코드를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const loadRegisteredGames = async (codeList: UploadCode[], teacherUid: string) => {
    const ownCodes = codeList
      .filter((item) => item.createdBy === teacherUid)
      .map((item) => item.code);
    if (ownCodes.length === 0) {
      setRegisteredGames([]);
      return;
    }
    setGamesLoading(true);
    try {
      const gamesRef = collection(db, 'games');
      const chunks: string[][] = [];
      for (let index = 0; index < ownCodes.length; index += 10) {
        chunks.push(ownCodes.slice(index, index + 10));
      }
      const snapshots = await Promise.all(
        chunks.map((chunk) => getDocs(query(gamesRef, where('uploadCode', 'in', chunk))))
      );
      const games = snapshots.flatMap((snapshot) => snapshot.docs.flatMap((item) => {
        const data = item.data();
        if (data.guestUpload !== true) return [];
        const game: CodeRegisteredGame = {
          id: item.id,
          title: data.title || '',
          description: data.description || '',
          introduction: data.introduction || '',
          gameUrl: data.gameUrl || '',
          simulatorCode: data.simulatorCode || '',
          processUrl: data.processUrl || '',
          companyName: data.companyName || '',
          creatorName: data.creatorName || '',
          grade: data.grade != null && data.grade !== '' ? String(data.grade) : '',
          className: data.class != null && data.class !== '' ? String(data.class) : '',
          commentsOnly: Boolean(data.commentsOnly),
          allowGuestComments: Boolean(data.allowGuestComments),
          uploadCode: data.uploadCode || '',
          iconUrl: data.iconUrl || '',
          createdAt: data.createdAt?.toDate?.() || new Date(),
        };
        return [game];
      })).sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      setRegisteredGames(games);
    } catch (loadError) {
      console.error('코드 등록 게임 목록 오류:', loadError);
      setError('코드로 등록된 게임을 불러오지 못했습니다.');
    } finally {
      setGamesLoading(false);
    }
  };

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i += 1) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  const handleCreate = async () => {
    if (!currentUser || !isTeacherUser) return;
    if (!currentUser.schoolId) {
      setError('학교가 지정된 교사 계정에서 코드를 만들 수 있습니다.');
      return;
    }
    const affiliationName = affiliation.trim();
    if (!affiliationName) {
      setError('소속을 입력해 주세요.');
      return;
    }
    setCreating(true);
    setError('');
    setMessage('');
    try {
      const code = generateCode();
      await setDoc(doc(db, GAME_UPLOAD_CODE_COLLECTION, code), {
        code,
        schoolId: currentUser.schoolId,
        schoolName: affiliationName,
        memo: memo.trim(),
        createdBy: currentUser.uid,
        createdAt: Timestamp.fromDate(new Date()),
      });
      setMemo('');
      setMessage(`${code} 코드를 만들었습니다. 학생에게 이 코드만 알려 주면 됩니다.`);
      await loadCodes();
    } catch (createError) {
      console.error('등록 코드 생성 오류:', createError);
      setError('코드를 만들지 못했습니다.');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (code: string) => {
    if (!window.confirm('이 코드를 삭제하면 더 이상 게임 등록에 쓸 수 없습니다. 삭제할까요?')) return;
    try {
      await deleteDoc(doc(db, GAME_UPLOAD_CODE_COLLECTION, normalizeTeacherCode(code)));
      setMessage('코드를 삭제했습니다.');
      await loadCodes();
    } catch (deleteError) {
      console.error('등록 코드 삭제 오류:', deleteError);
      setError('코드를 삭제하지 못했습니다.');
    }
  };

  const startGameEdit = (game: CodeRegisteredGame) => {
    setEditingGameId(game.id);
    setEditForm({
      title: game.title,
      description: game.description,
      introduction: game.introduction,
      gameUrl: game.gameUrl,
      simulatorCode: game.simulatorCode,
      processUrl: game.processUrl,
      companyName: game.companyName,
      creatorName: game.creatorName,
      grade: game.grade,
      className: game.className,
      commentsOnly: game.commentsOnly,
      allowGuestComments: game.allowGuestComments,
    });
    setIconFile(null);
    setIconPreview(game.iconUrl || null);
    setError('');
    setMessage('');
  };

  const handleIconChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('이미지 파일만 업로드할 수 있습니다.');
      return;
    }
    setIconFile(file);
    const reader = new FileReader();
    reader.onload = () => setIconPreview(typeof reader.result === 'string' ? reader.result : null);
    reader.readAsDataURL(file);
  };

  const handleSaveGame = async () => {
    if (!editingGameId || !currentUser) return;
    setSavingGame(true);
    setError('');
    setMessage('');
    try {
      let iconUrl = '';
      if (iconFile && storage) {
        const storageRef = ref(storage, `game-icons/${currentUser.uid}/${Date.now()}_${iconFile.name}`);
        await uploadBytes(storageRef, iconFile);
        iconUrl = await getDownloadURL(storageRef);
      }
      const payload = toCodeIssuerGameUpdate({
        title: editForm.title,
        description: editForm.description,
        introduction: editForm.introduction,
        gameUrl: editForm.gameUrl,
        simulatorCode: editForm.simulatorCode,
        processUrl: editForm.processUrl,
        companyName: editForm.companyName,
        creatorName: editForm.creatorName,
        grade: editForm.grade ? parseInt(editForm.grade, 10) : null,
        class: editForm.className ? parseInt(editForm.className, 10) : null,
        commentsOnly: editForm.commentsOnly,
        allowGuestComments: editForm.allowGuestComments,
      }, iconUrl || undefined);
      await updateDoc(doc(db, 'games', editingGameId), payload);
      setMessage('게임을 수정했습니다. 평가용 게임으로는 바뀌지 않습니다.');
      setEditingGameId(null);
      setEditForm(emptyGameForm);
      setIconFile(null);
      setIconPreview(null);
      await loadRegisteredGames(codes, currentUser.uid);
    } catch (saveError) {
      console.error('코드 등록 게임 수정 오류:', saveError);
      const code = saveError && typeof saveError === 'object' && 'code' in saveError
        ? String((saveError as { code?: string }).code || '')
        : '';
      if (code.includes('permission-denied')) {
        setError('게임을 수정하지 못했습니다. 이 코드를 발급한 계정으로 로그인했는지 확인해 주세요.');
      } else if (saveError instanceof Error && saveError.message) {
        setError(saveError.message);
      } else {
        setError('게임을 수정하지 못했습니다.');
      }
    } finally {
      setSavingGame(false);
    }
  };

  const handleCopy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setMessage(`${code} 코드를 복사했습니다.`);
      setError('');
    } catch (copyError) {
      console.error('코드 복사 오류:', copyError);
      setError('복사에 실패했습니다. 코드를 직접 선택해 복사해 주세요.');
    }
  };

  if (authLoading || !currentUser || !isTeacherUser || loading) {
    return (
      <MainLayout>
        <div style={{ ...getMakecodeArcadePageRootStyle(isMobile), textAlign: 'center' }}>불러오는 중...</div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div style={getMakecodeArcadePageRootStyle(isMobile)}>
        <h1 style={{ fontSize: '24px', fontWeight: 600, margin: '0 0 8px', color: '#F4F4F8', fontFamily: 'Pretendard' }}>
          등록 코드
        </h1>
        <p style={{ margin: '0 0 24px', fontSize: '14px', color: '#C8C8D4', lineHeight: 1.5, fontFamily: 'Pretendard' }}>
          학생에게 이 코드를 알려 주면, 회원가입 없이 게임 등록 페이지에서 자신의 게임을 올릴 수 있습니다.
          내가 발급한 코드로 올라온 게임은 아래에서 수정할 수 있습니다. 평가용 게임으로는 바뀌지 않습니다.{' '}
          <button
            type="button"
            onClick={() => navigate('/makecodearcade/register-guide')}
            style={{
              padding: 0,
              border: 'none',
              background: 'none',
              color: '#FF6F0F',
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
              textDecoration: 'underline',
              fontFamily: 'Pretendard',
            }}
          >
            등록 가이드 보기
          </button>
        </p>

        {error && (
          <div style={{ marginBottom: 16, padding: '12px 16px', background: '#FFF4F4', color: '#C33333', borderRadius: 8, fontSize: 14 }}>
            {error}
          </div>
        )}
        {message && (
          <div style={{ marginBottom: 16, padding: '12px 16px', background: '#F0FDF4', color: '#166534', borderRadius: 8, fontSize: 14 }}>
            {message}
          </div>
        )}

        <Card padding="20px" style={{ marginBottom: 24, color: '#161616' }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>새 코드</div>
          <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>소속 *</label>
          <input
            value={affiliation}
            onChange={(event) => setAffiliation(event.target.value)}
            placeholder="예: 서울선린중학교"
            maxLength={40}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #F0F0F0',
              borderRadius: 8,
              fontSize: 14,
              fontFamily: 'Pretendard',
              boxSizing: 'border-box',
              marginBottom: 8,
              backgroundColor: '#FFFFFF',
              color: '#161616',
            }}
          />
          <div style={{ fontSize: 12, color: '#989898', marginBottom: 12 }}>
            이 코드로 게임을 등록하면 여기에 입력한 소속이 표시됩니다.
          </div>
          <input
            value={memo}
            onChange={(event) => setMemo(event.target.value)}
            placeholder="메모 (선택) 예: 3학년 게임 시간"
            maxLength={40}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #F0F0F0',
              borderRadius: 8,
              fontSize: 14,
              fontFamily: 'Pretendard',
              boxSizing: 'border-box',
              marginBottom: 12,
              backgroundColor: '#FFFFFF',
              color: '#161616',
            }}
          />
          <Button variant="primary" onClick={handleCreate} disabled={creating}>
            {creating ? '만드는 중...' : '코드 만들기'}
          </Button>
        </Card>

        <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 16px', color: '#F4F4F8', fontFamily: 'Pretendard' }}>
          만든 코드
        </h2>
        {codes.length === 0 ? (
          <div style={{ padding: '12px 16px', background: '#F8F8F8', borderRadius: 8, fontSize: 14, color: '#666' }}>
            아직 만든 코드가 없습니다.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {codes.map((item) => (
              <Card key={item.code} padding="16px" style={{ color: '#161616' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 700, letterSpacing: '0.04em' }}>{item.code}</div>
                    <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                      소속 {item.schoolName}{item.memo ? ` · ${item.memo}` : ''} · {item.createdAt.toLocaleDateString()}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button variant="outline" size="small" onClick={() => handleCopy(item.code)}>복사</Button>
                    <Button variant="cancel" size="small" onClick={() => handleDelete(item.code)}>삭제</Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        <h2 style={{ fontSize: 18, fontWeight: 600, margin: '32px 0 8px', color: '#F4F4F8', fontFamily: 'Pretendard' }}>
          코드로 등록된 게임
        </h2>
        <p style={{ margin: '0 0 16px', fontSize: 14, color: '#C8C8D4', lineHeight: 1.5, fontFamily: 'Pretendard' }}>
          내가 발급한 코드로 등록된 게임만 표시됩니다. 등록한 사람과 소속, 코드는 바뀌지 않습니다.
        </p>
        {gamesLoading ? (
          <div style={{ padding: '12px 16px', background: '#F8F8F8', borderRadius: 8, fontSize: 14, color: '#666' }}>
            게임을 불러오는 중...
          </div>
        ) : registeredGames.length === 0 ? (
          <div style={{ padding: '12px 16px', background: '#F8F8F8', borderRadius: 8, fontSize: 14, color: '#666' }}>
            내가 발급한 코드로 등록된 게임이 없습니다.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {registeredGames.map((game) => (
              <Card key={game.id} padding="16px" style={{ color: '#161616' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 16, fontWeight: 700 }}>{game.title}</div>
                    <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                      제작 {game.creatorName || '이름 없음'} · 코드 {game.uploadCode}
                      {game.grade || game.className
                        ? ` · ${game.grade ? `${game.grade}학년` : ''}${game.className ? ` ${game.className}반` : ''}`
                        : ''}
                    </div>
                  </div>
                  <Button variant="outline" size="small" onClick={() => startGameEdit(game)}>수정</Button>
                </div>
                {editingGameId === game.id && (
                  <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ fontSize: 13, color: '#666', lineHeight: 1.5 }}>
                      평가용 게임으로는 변경되지 않습니다. 아이콘은 로그인한 교사만 바꿀 수 있습니다.
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <img
                        src={iconPreview || '/images/icons/Logo.svg'}
                        alt="게임 아이콘"
                        style={{ width: 64, height: 64, borderRadius: 8, objectFit: 'cover', background: '#F5F5F5' }}
                      />
                      <div>
                        <input
                          ref={iconInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleIconChange}
                          style={{ display: 'none' }}
                        />
                        <Button variant="outline" size="small" onClick={() => iconInputRef.current?.click()}>
                          아이콘 바꾸기
                        </Button>
                      </div>
                    </div>
                    <label style={{ fontSize: 14, fontWeight: 500 }}>게임 제목 *</label>
                    <input value={editForm.title} onChange={(event) => setEditForm({ ...editForm, title: event.target.value })} style={fieldStyle} />
                    <label style={{ fontSize: 14, fontWeight: 500 }}>게임 설명</label>
                    <textarea value={editForm.description} onChange={(event) => setEditForm({ ...editForm, description: event.target.value })} rows={3} style={{ ...fieldStyle, minHeight: 72 }} />
                    <label style={{ fontSize: 14, fontWeight: 500 }}>게임 소개 문구</label>
                    <textarea value={editForm.introduction} onChange={(event) => setEditForm({ ...editForm, introduction: event.target.value })} rows={2} style={{ ...fieldStyle, minHeight: 60 }} />
                    <label style={{ fontSize: 14, fontWeight: 500 }}>게임 URL *</label>
                    <input value={editForm.gameUrl} onChange={(event) => setEditForm({ ...editForm, gameUrl: event.target.value })} style={fieldStyle} />
                    <label style={{ fontSize: 14, fontWeight: 500 }}>시뮬레이터 코드 *</label>
                    <textarea value={editForm.simulatorCode} onChange={(event) => setEditForm({ ...editForm, simulatorCode: event.target.value })} rows={4} style={{ ...fieldStyle, minHeight: 80 }} />
                    <label style={{ fontSize: 14, fontWeight: 500 }}>제작 과정 페이지 URL</label>
                    <input value={editForm.processUrl} onChange={(event) => setEditForm({ ...editForm, processUrl: event.target.value })} style={fieldStyle} />
                    <label style={{ fontSize: 14, fontWeight: 500 }}>제작 회사명</label>
                    <input value={editForm.companyName} onChange={(event) => setEditForm({ ...editForm, companyName: event.target.value })} style={fieldStyle} />
                    <label style={{ fontSize: 14, fontWeight: 500 }}>제작자 이름 *</label>
                    <input value={editForm.creatorName} onChange={(event) => setEditForm({ ...editForm, creatorName: event.target.value })} style={fieldStyle} />
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 120 }}>
                        <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>학년</label>
                        <select value={editForm.grade} onChange={(event) => setEditForm({ ...editForm, grade: event.target.value })} style={fieldStyle}>
                          <option value="">선택하지 않음</option>
                          {numberChoices(6, editForm.grade).map((grade) => (
                            <option key={grade} value={grade}>{grade}학년</option>
                          ))}
                        </select>
                      </div>
                      <div style={{ flex: 1, minWidth: 120 }}>
                        <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>반</label>
                        <select value={editForm.className} onChange={(event) => setEditForm({ ...editForm, className: event.target.value })} style={fieldStyle}>
                          <option value="">선택하지 않음</option>
                          {numberChoices(10, editForm.className).map((className) => (
                            <option key={className} value={className}>{className}반</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                      <input
                        type="checkbox"
                        checked={editForm.commentsOnly}
                        onChange={(event) => setEditForm({ ...editForm, commentsOnly: event.target.checked })}
                      />
                      별점 없이 댓글만 받기
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                      <input
                        type="checkbox"
                        checked={editForm.allowGuestComments}
                        onChange={(event) => setEditForm({ ...editForm, allowGuestComments: event.target.checked })}
                      />
                      로그인 없이 별점·리뷰 등록 허용
                    </label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button variant="primary" onClick={handleSaveGame} disabled={savingGame}>
                        {savingGame ? '저장 중...' : '수정 내용 저장'}
                      </Button>
                      <Button variant="outline" onClick={() => { setEditingGameId(null); setIconFile(null); }}>
                        취소
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default UploadCodePage;
